// ============================================================
// FILE: src/pages/ReportBooksPage.jsx
// Mobile-first redesign — clean, professional, no hidden buttons
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, ReferenceLine, CartesianGrid,
} from 'recharts';
import { toast } from 'react-toastify';

const P  = '#6B21A8';
const PM = '#7C3AED';
const PL = '#EDE9FE';
const GR = '#16A34A';
const RD = '#DC2626';
const AM = '#D97706';

const BOOKS = [
  { key:'OLEIN',    title:'Finished Product — Olein',             icon:'🛢️', color:'#0369A1', light:'#E0F2FE', desc:'Filling & Packing · Tanks 411, 412, 413, 414',            filter:(r)=>['OIL_T411','OIL_T412','OIL_T413','OIL_T414'].includes(r.sample_types?.code) },
  { key:'FATS',     title:'Edible Vegetable Cooking Fats',         icon:'📦', color:'#7C2D12', light:'#FFF7ED', desc:'Filling & Packing · Chipo, Cowboy, Kimbo, Chipsy',          filter:(r)=>['FAT_CHIPO','FAT_COWBOY','FAT_KIMBO','FAT_CHIPSY'].includes(r.sample_types?.code) },
  { key:'VITAMIN_A',title:'Daily Vitamin A Analysis',              icon:'💊', color:'#0891B2', light:'#ECFEFF', desc:'All departments · All Vitamin A tested samples',             filter:()=>true, testFilter:'Vitamin A' },
  { key:'SOAP',     title:'Soap Analysis (Chemicals)',             icon:'🧼', color:'#059669', light:'#ECFDF5', desc:'Soap Dept · All laundry bar soap brands',                   filter:(r)=>r.sample_types?.code?.startsWith('LBS_') },
  { key:'REF_INP',  title:'Lab Report — In-process Analysis',     icon:'🏭', color:'#7C3AED', light:'#EDE9FE', desc:'Refinery · CPO, CPL, BPO, RBD, RPL, PFAD lines',          filter:(r)=>['CPO_LINE','CPL_LINE','BPO','RBD','RPL','PFAD'].includes(r.sample_types?.code) },
  { key:'CRYS',     title:'Crystallizer Analysis Records',         icon:'❄️', color:'#0284C7', light:'#E0F2FE', desc:'Refinery · Crystallizers 1–9',                             filter:(r)=>r.sample_types?.code?.startsWith('CRYS_') },
  { key:'FRAC',     title:'Fractionation Analysis',                icon:'🔬', color:'#9333EA', light:'#F3E8FF', desc:'Refinery · OLEIN, STEARIN, PMF',                           filter:(r)=>['OLEIN','STEARIN','PMF'].includes(r.sample_types?.code) },
];

const STATUS_CFG = {
  pass     : { color:GR,  bg:'#F0FDF4', border:'#86EFAC', label:'PASS' },
  ok       : { color:GR,  bg:'#F0FDF4', border:'#86EFAC', label:'OK'   },
  fail_low : { color:RD,  bg:'#FEF2F2', border:'#FECACA', label:'LOW ↓'},
  fail_high: { color:RD,  bg:'#FEF2F2', border:'#FECACA', label:'HIGH ↑'},
  text_ok  : { color:'#1D4ED8', bg:'#EFF6FF', border:'#BFDBFE', label:'OK' },
};
const getCS = (s) => STATUS_CFG[s] || { color:'#64748B', bg:'#F8FAFC', border:'#E2E8F0', label:'—' };

export default function ReportBooksPage() {
  const { user }       = useAuth();
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const [activeBook,  setActiveBook]  = useState(BOOKS.find(b => b.key === searchParams.get('book')) || BOOKS[0]);
  const [showBookList,setShowBookList]= useState(false);
  const [date,        setDate]        = useState(format(subDays(new Date(),1),'yyyy-MM-dd'));
  const [samples,     setSamples]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [signoff,     setSignoff]     = useState(null);
  const [comment,     setComment]     = useState('');
  const [signature,   setSignature]   = useState(user?.full_name || '');
  const [signing,     setSigning]     = useState(false);
  const [trendData,   setTrendData]   = useState([]);
  const [selTest,     setSelTest]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('registered_samples')
        .select(`
          id, sample_number, sample_name, status, registered_at, sampler_name,
          departments(name, code),
          sample_types(name, code),
          sample_subtypes(name),
          sample_test_assignments(
            id, result_value, result_status, remarks,
            submitted_at, analyst_signature, edit_count, is_locked,
            tests(id, name, unit, result_type, display_order,
              test_specifications(min_value, max_value, display_spec))
          )
        `)
        .gte('registered_at', startOfDay(new Date(date)).toISOString())
        .lte('registered_at', endOfDay(new Date(date)).toISOString())
        .order('registered_at', { ascending: true });

      let filtered = (data || []).filter(activeBook.filter);
      if (activeBook.testFilter) {
        filtered = filtered.filter(s =>
          (s.sample_test_assignments||[]).some(a => a.tests?.name === activeBook.testFilter && a.result_value)
        );
      }
      setSamples(filtered);

      const { data: existing } = await supabase
        .from('report_signoffs')
        .select('*')
        .eq('report_book', activeBook.key)
        .eq('report_date', date)
        .single();
      setSignoff(existing || null);

      if (filtered.length > 0 && !selTest) {
        const first = filtered[0]?.sample_test_assignments?.[0]?.tests?.name;
        if (first) setSelTest(first);
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [activeBook, date, selTest]);

  useEffect(() => { load(); }, [load]);

  // Build trend
  useEffect(() => {
    if (!selTest || !samples.length) return;
    const pts = [];
    for (const s of samples) {
      for (const a of (s.sample_test_assignments||[])) {
        if (a.tests?.name === selTest && a.result_value) {
          const num = parseFloat(a.result_value);
          if (!isNaN(num)) {
            const sp = a.tests?.test_specifications?.[0];
            pts.push({ name: s.sample_name?.substring(0,10), value: num, min: sp?.min_value||null, max: sp?.max_value||null, time: a.submitted_at ? format(new Date(a.submitted_at),'HH:mm') : '' });
          }
        }
      }
    }
    setTrendData(pts);
  }, [selTest, samples]);

  const handleSignoff = async (status) => {
    if (!signature.trim()) { toast.warning('Enter your name as signature'); return; }
    setSigning(true);
    try {
      await supabase.from('report_signoffs').upsert({
        report_book: activeBook.key, report_date: date,
        signed_by: user?.id, status,
        comments: comment.trim() || null,
        signature_text: signature.trim(),
        signed_at: new Date().toISOString(),
      }, { onConflict: 'report_book,report_date' });
      toast.success(status==='approved' ? '✅ Report approved and signed' : '⚠️ Report flagged');
      load();
    } catch(e) { toast.error('Sign-off failed: ' + e.message); }
    finally { setSigning(false); }
  };

  const allTestNames = [...new Set(
    samples.flatMap(s => (s.sample_test_assignments||[]).filter(a=>a.result_value).map(a=>a.tests?.name).filter(Boolean))
  )];

  const totalResults = samples.reduce((n,s) => n + (s.sample_test_assignments||[]).filter(a=>a.result_value).length, 0);
  const totalOOS     = samples.reduce((n,s) => n + (s.sample_test_assignments||[]).filter(a=>a.result_status==='fail_low'||a.result_status==='fail_high').length, 0);
  const passRate     = totalResults > 0 ? Math.round(((totalResults-totalOOS)/totalResults)*100) : 100;

  return (
    <div style={{ minHeight:'100vh', background:'#F5F3FF', paddingBottom: isMobile ? 'calc(80px + 20px)' : '40px' }}>

      {/* ── TOP NAV ── */}
      <div style={{ background:`linear-gradient(135deg,${P},${PM})`, padding:'0 14px', display:'flex', alignItems:'center', gap:'8px', height:'52px', position:'sticky', top:0, zIndex:100, boxShadow:'0 2px 12px rgba(107,33,168,0.35)' }}>
        <button onClick={()=>navigate(-1)}
          style={{ padding:'5px 10px', background:'rgba(255,255,255,0.15)', border:'none', borderRadius:'7px', color:'#fff', fontSize:'12px', fontWeight:'700', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
          ← Back
        </button>
        <div style={{ flex:1, textAlign:'center' }}>
          <div style={{ color:'#fff', fontWeight:'800', fontSize:'13px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
            {activeBook.icon} {activeBook.title}
          </div>
        </div>
        <button onClick={()=>setShowBookList(!showBookList)}
          style={{ padding:'5px 10px', background:'rgba(255,255,255,0.15)', border:'none', borderRadius:'7px', color:'#fff', fontSize:'11px', fontWeight:'700', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
          📚 Books {showBookList?'▲':'▼'}
        </button>
      </div>

      {/* ── BOOK SELECTOR DROPDOWN ── */}
      {showBookList && (
        <div style={{ position:'sticky', top:'52px', zIndex:99, background:'#fff', borderBottom:`1.5px solid ${PL}`, boxShadow:'0 4px 16px rgba(107,33,168,0.12)' }}>
          {BOOKS.map(b => (
            <button key={b.key} onClick={()=>{ setActiveBook(b); setShowBookList(false); setSelTest(''); }}
              style={{ display:'flex', alignItems:'center', gap:'12px', width:'100%', padding:'11px 16px', border:'none', borderBottom:`1px solid ${PL}`, background: activeBook.key===b.key ? b.light : '#fff', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
              <span style={{ fontSize:'20px', flexShrink:0 }}>{b.icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:'800', fontSize:'13px', color: activeBook.key===b.key ? b.color : '#0F172A' }}>{b.title}</div>
                <div style={{ fontSize:'11px', color:'#94A3B8', marginTop:'1px' }}>{b.desc}</div>
              </div>
              {activeBook.key===b.key && <span style={{ color:b.color, fontSize:'14px' }}>✓</span>}
            </button>
          ))}
        </div>
      )}

      <div style={{ padding: isMobile ? '10px' : '16px', maxWidth:'1100px', margin:'0 auto' }}>

        {/* ── DATE + STATS BAR ── */}
        <div style={{ background:'#fff', borderRadius:'12px', border:`1.5px solid ${PL}`, padding:'10px 14px', marginBottom:'10px', display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', flex:1, minWidth:'200px' }}>
            <span style={{ fontSize:'11px', fontWeight:'700', color:'#64748B', whiteSpace:'nowrap' }}>Report Date:</span>
            <input type="date" value={date} onChange={e=>{setDate(e.target.value);setSelTest('');}}
              style={{ border:`1.5px solid ${PL}`, borderRadius:'8px', padding:'5px 9px', fontSize:'12px', fontFamily:'inherit', cursor:'pointer', outline:'none', flex:1 }}/>
          </div>
          {/* Quick date shortcuts */}
          <div style={{ display:'flex', gap:'4px' }}>
            {[{l:'Today',d:0},{l:'Yest',d:1},{l:'2d ago',d:2}].map(q=>(
              <button key={q.l} onClick={()=>{setDate(format(subDays(new Date(),q.d),'yyyy-MM-dd'));setSelTest('');}}
                style={{ padding:'4px 9px', border:`1.5px solid ${PL}`, borderRadius:'7px', background:'#F5F3FF', color:P, fontSize:'10px', fontWeight:'700', cursor:'pointer' }}>
                {q.l}
              </button>
            ))}
          </div>
          {/* Inline stats pills */}
          <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
            {[
              { label:'Samples',  val:samples.length,  color:'#0F172A', bg:'#fff' },
              { label:'Results',  val:totalResults,    color:PM,        bg:'#F5F3FF' },
              { label:'OOS',      val:totalOOS,        color:totalOOS>0?RD:'#94A3B8', bg:totalOOS>0?'#FEF2F2':'#fff' },
              { label:'Pass',     val:`${passRate}%`,  color:passRate>=95?GR:passRate>=80?AM:RD, bg:'#F0FDF4' },
            ].map(s=>(
              <div key={s.label} style={{ background:s.bg, border:`1px solid ${PL}`, borderRadius:'20px', padding:'3px 10px', display:'flex', alignItems:'center', gap:'4px' }}>
                <span style={{ fontSize:'13px', fontWeight:'900', color:s.color }}>{s.val}</span>
                <span style={{ fontSize:'9px', color:'#94A3B8', fontWeight:'700', textTransform:'uppercase' }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── SIGN-OFF STATUS BANNER ── */}
        {signoff && (
          <div style={{ background: signoff.status==='approved'?'#F0FDF4':'#FFFBEB', border:`1.5px solid ${signoff.status==='approved'?'#86EFAC':'#FDE68A'}`, borderRadius:'12px', padding:'10px 14px', marginBottom:'10px', display:'flex', alignItems:'center', gap:'10px' }}>
            <span style={{ fontSize:'20px' }}>{signoff.status==='approved'?'✅':'⚠️'}</span>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:'800', fontSize:'13px', color:signoff.status==='approved'?GR:AM }}>
                {signoff.status==='approved'?'Approved':'Flagged'} by {signoff.signature_text}
              </div>
              <div style={{ fontSize:'11px', color:'#64748B' }}>
                {format(new Date(signoff.signed_at),'dd MMM yyyy HH:mm')}
                {signoff.comments && ` — "${signoff.comments}"`}
              </div>
            </div>
          </div>
        )}

        {/* ── LOADING / EMPTY ── */}
        {loading ? (
          <div style={{ textAlign:'center', padding:'60px', background:'#fff', borderRadius:'14px', border:`1.5px solid ${PL}` }}>
            <div style={{ fontSize:'32px', marginBottom:'10px' }}>⏳</div>
            <div style={{ fontSize:'13px', color:'#94A3B8', fontWeight:'600' }}>Loading report...</div>
          </div>
        ) : samples.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px', background:'#fff', borderRadius:'14px', border:`1.5px solid ${PL}` }}>
            <div style={{ fontSize:'40px', marginBottom:'12px' }}>📭</div>
            <div style={{ fontWeight:'700', fontSize:'15px', color:'#374151' }}>No samples for this date</div>
            <div style={{ fontSize:'12px', color:'#94A3B8', marginTop:'6px' }}>Try a different date or check another report book</div>
          </div>
        ) : (
          <>
            {/* ── RESULTS TABLE — horizontal scroll on mobile ── */}
            <div style={{ background:'#fff', borderRadius:'14px', border:`1.5px solid ${PL}`, marginBottom:'10px', overflow:'hidden' }}>
              <div style={{ padding:'10px 14px', borderBottom:`1px solid ${PL}`, display:'flex', alignItems:'center', justifyContent:'space-between', background:`linear-gradient(135deg,${activeBook.color}11,#fff)` }}>
                <div style={{ fontWeight:'800', fontSize:'13px', color:activeBook.color }}>
                  {activeBook.icon} Results — {format(new Date(date),'dd MMMM yyyy')}
                </div>
                <span style={{ fontSize:'11px', color:'#94A3B8' }}>{samples.length} sample{samples.length!==1?'s':''}</span>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px', minWidth: isMobile ? '600px' : 'auto' }}>
                  <thead>
                    <tr style={{ background:'#F8FAFC' }}>
                      <th style={{ padding:'8px 10px', textAlign:'left', fontSize:'10px', fontWeight:'700', color:'#64748B', borderBottom:'1px solid #E2E8F0', whiteSpace:'nowrap', position:'sticky', left:0, background:'#F8FAFC', zIndex:2 }}>Sample</th>
                      <th style={{ padding:'8px 10px', textAlign:'left', fontSize:'10px', fontWeight:'700', color:'#64748B', borderBottom:'1px solid #E2E8F0', whiteSpace:'nowrap' }}>Type</th>
                      <th style={{ padding:'8px 10px', textAlign:'left', fontSize:'10px', fontWeight:'700', color:'#64748B', borderBottom:'1px solid #E2E8F0', whiteSpace:'nowrap' }}>Status</th>
                      {allTestNames.map(tn => (
                        <th key={tn} style={{ padding:'8px 10px', textAlign:'center', fontSize:'10px', fontWeight:'700', color:'#64748B', borderBottom:'1px solid #E2E8F0', whiteSpace:'nowrap', borderLeft:`2px solid ${PL}` }}>{tn}</th>
                      ))}
                      <th style={{ padding:'8px 10px', textAlign:'left', fontSize:'10px', fontWeight:'700', color:'#64748B', borderBottom:'1px solid #E2E8F0', whiteSpace:'nowrap', borderLeft:`2px solid ${PL}` }}>Analyst</th>
                    </tr>
                  </thead>
                  <tbody>
                    {samples.map((s, i) => {
                      const byTest = {};
                      (s.sample_test_assignments||[]).forEach(a => { if (a.tests?.name) byTest[a.tests.name] = a; });
                      const analysts = [...new Set((s.sample_test_assignments||[]).map(a=>a.analyst_signature).filter(Boolean))];
                      const hasOOS   = (s.sample_test_assignments||[]).some(a=>a.result_status==='fail_low'||a.result_status==='fail_high');
                      const rowBg    = hasOOS ? '#FFF5F5' : i%2===0 ? '#fff' : '#FAFBFC';
                      return (
                        <tr key={s.id}>
                          <td style={{ padding:'8px 10px', borderBottom:'1px solid #F1F5F9', background:rowBg, fontWeight:'700', color:'#0F172A', whiteSpace:'nowrap', position:'sticky', left:0, zIndex:1 }}>
                            <div style={{ fontSize:'12px' }}>{s.sample_name}</div>
                            <div style={{ fontSize:'10px', color:'#94A3B8', fontFamily:'monospace' }}>{s.sample_number}</div>
                          </td>
                          <td style={{ padding:'8px 10px', borderBottom:'1px solid #F1F5F9', background:rowBg, whiteSpace:'nowrap' }}>
                            <span style={{ fontSize:'10px', color:'#64748B' }}>{s.sample_types?.name||'—'}</span>
                          </td>
                          <td style={{ padding:'8px 10px', borderBottom:'1px solid #F1F5F9', background:rowBg }}>
                            <span style={{ fontSize:'10px', fontWeight:'700', padding:'2px 7px', borderRadius:'5px',
                              background: s.status==='complete'?'#F0FDF4':s.status==='in_progress'?'#FFFBEB':'#F8FAFC',
                              color: s.status==='complete'?GR:s.status==='in_progress'?AM:'#64748B' }}>
                              {s.status==='complete'?'Done':s.status==='in_progress'?'Running':'Pending'}
                            </span>
                          </td>
                          {allTestNames.map(tn => {
                            const a = byTest[tn];
                            if (!a || !a.result_value) return (
                              <td key={tn} style={{ padding:'8px 10px', textAlign:'center', background:rowBg, borderLeft:`2px solid ${PL}`, borderBottom:'1px solid #F1F5F9', color:'#CBD5E1', fontSize:'13px' }}>—</td>
                            );
                            const cs = getCS(a.result_status);
                            return (
                              <td key={tn} style={{ padding:'6px 8px', textAlign:'center', background:rowBg, borderLeft:`2px solid ${PL}`, borderBottom:'1px solid #F1F5F9' }}>
                                <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', background:cs.bg, color:cs.color, border:`1.5px solid ${cs.border}`, borderRadius:'7px', padding:'3px 8px', minWidth:'44px' }}>
                                  <span style={{ fontWeight:'900', fontSize:'13px' }}>{a.result_value}{a.tests?.unit&&<span style={{ fontSize:'9px', opacity:0.8 }}>{a.tests.unit}</span>}</span>
                                  {(a.result_status==='fail_low'||a.result_status==='fail_high') && (
                                    <span style={{ fontSize:'9px', fontWeight:'800' }}>{cs.label}</span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                          <td style={{ padding:'8px 10px', borderBottom:'1px solid #F1F5F9', background:rowBg, borderLeft:`2px solid ${PL}`, fontSize:'11px', color:'#64748B', whiteSpace:'nowrap' }}>
                            {analysts.join(', ')||'—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── TREND CHART ── */}
            {trendData.length > 1 && (
              <div style={{ background:'#fff', borderRadius:'14px', border:`1.5px solid ${PL}`, padding:'14px', marginBottom:'10px' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px', flexWrap:'wrap', gap:'8px' }}>
                  <div style={{ fontWeight:'800', fontSize:'13px', color:'#0F172A' }}>📈 Results Trend</div>
                  <select value={selTest} onChange={e=>setSelTest(e.target.value)}
                    style={{ border:`1.5px solid ${PL}`, borderRadius:'8px', padding:'5px 10px', fontSize:'12px', fontFamily:'inherit', cursor:'pointer', color:'#374151', background:'#fff', outline:'none' }}>
                    {allTestNames.map(tn=><option key={tn} value={tn}>{tn}</option>)}
                  </select>
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={trendData} margin={{ top:5, right:10, left:-20, bottom:0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false}/>
                    <XAxis dataKey="name" tick={{ fontSize:9, fill:'#94A3B8' }} axisLine={false} tickLine={false}/>
                    <YAxis tick={{ fontSize:9, fill:'#94A3B8' }} axisLine={false} tickLine={false}/>
                    <Tooltip contentStyle={{ borderRadius:'10px', border:`1px solid ${PL}`, fontSize:'11px' }} formatter={v=>[v, selTest]}/>
                    {trendData[0]?.min != null && <ReferenceLine y={trendData[0].min} stroke={RD} strokeDasharray="4 3" label={{ value:'Min', fill:RD, fontSize:9 }}/>}
                    {trendData[0]?.max != null && <ReferenceLine y={trendData[0].max} stroke={RD} strokeDasharray="4 3" label={{ value:'Max', fill:RD, fontSize:9 }}/>}
                    <Line type="monotone" dataKey="value" stroke={PM} strokeWidth={2} dot={{ fill:PM, r:3 }} activeDot={{ r:5 }}/>
                  </LineChart>
                </ResponsiveContainer>
                <div style={{ fontSize:'10px', color:'#94A3B8', textAlign:'center', marginTop:'6px' }}>Red dashed lines = specification limits</div>
              </div>
            )}

            {/* ── SIGN-OFF PANEL ── */}
            <div style={{ background:'#fff', borderRadius:'14px', border:`2px solid ${signoff?.status==='approved'?GR+'44':PL}`, padding:'14px' }}>
              <div style={{ fontWeight:'800', fontSize:'14px', color:'#0F172A', marginBottom:'4px' }}>✍️ QC Head Sign-Off</div>
              <div style={{ fontSize:'11px', color:'#64748B', marginBottom:'14px' }}>
                Review all results above then approve or flag this report for <strong>{format(new Date(date),'dd MMMM yyyy')}</strong>. This is recorded permanently for audit purposes.
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'14px' }}>
                <div>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#4C1D95', marginBottom:'4px' }}>Your Signature (Full Name) *</label>
                  <input type="text" value={signature} onChange={e=>setSignature(e.target.value)} placeholder="Type your full name"
                    style={{ border:`1.5px solid ${PL}`, borderRadius:'8px', padding:'9px 12px', fontSize:'13px', fontFamily:'inherit', color:'#111827', outline:'none', width:'100%', boxSizing:'border-box' }}/>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:'#4C1D95', marginBottom:'4px' }}>Comments / Observations</label>
                  <textarea value={comment} onChange={e=>setComment(e.target.value)} placeholder="Any observations, instructions or flags for this report..."
                    style={{ border:`1.5px solid ${PL}`, borderRadius:'8px', padding:'9px 12px', fontSize:'13px', fontFamily:'inherit', color:'#111827', outline:'none', width:'100%', boxSizing:'border-box', resize:'vertical', minHeight:'56px' }}/>
                </div>
              </div>

              {/* Sign-off buttons — always visible above bottom nav */}
              <div style={{ display:'flex', gap:'10px' }}>
                <button onClick={()=>handleSignoff('approved')} disabled={signing}
                  style={{ flex:1, padding:'12px', background:signing?'#A7F3D0':`linear-gradient(135deg,${GR},#15803D)`, color:'#fff', border:'none', borderRadius:'10px', fontSize:'13px', fontWeight:'700', cursor:signing?'not-allowed':'pointer', fontFamily:'inherit' }}>
                  {signing ? '⏳ Signing...' : '✅ Approve & Sign'}
                </button>
                <button onClick={()=>handleSignoff('flagged')} disabled={signing}
                  style={{ flex:1, padding:'12px', background:signing?'#FED7AA':`linear-gradient(135deg,#EA580C,#C2410C)`, color:'#fff', border:'none', borderRadius:'10px', fontSize:'13px', fontWeight:'700', cursor:signing?'not-allowed':'pointer', fontFamily:'inherit' }}>
                  ⚠️ Flag for Review
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
